import { useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery, useGlobalMutation } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  getOrgMembers,
  getOrgInvitations,
  inviteMember,
  updateMemberRole,
  removeMember,
  revokeInvitation,
  resendInvitation,
  updateInvitationRoleAndResend,
} from '../services/org-users.service';
import type { InviteMemberInput, GetMembersParams } from '../types/org-users.types';
import type { OrgRole } from '../../org-permissions/types/permissions.types';

export const useGetOrgMembers = (
  blocksOrgId: string | undefined,
  params: GetMembersParams = {}
) => {
  return useGlobalQuery({
    queryKey: ['orgMembers', blocksOrgId, params],
    queryFn: () => {
      if (!blocksOrgId) return Promise.resolve({ members: [], totalCount: 0 });
      return getOrgMembers(blocksOrgId, params);
    },
    enabled: !!blocksOrgId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetOrgInvitations = (
  blocksOrgId: string | undefined,
  page = 0,
  pageSize = 50
) => {
  return useGlobalQuery({
    queryKey: ['orgInvitations', blocksOrgId, page, pageSize],
    queryFn: () => {
      if (!blocksOrgId) return Promise.resolve({ invitations: [], totalCount: 0 });
      return getOrgInvitations(blocksOrgId, page, pageSize);
    },
    enabled: !!blocksOrgId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useInviteMember = (blocksOrgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (data: InviteMemberInput) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return inviteMember(blocksOrgId, data);
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['orgMembers', blocksOrgId] });
        queryClient.invalidateQueries({ queryKey: ['orgInvitations', blocksOrgId] });
      }
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to send invitation' });
    },
  });
};

export const useUpdateMemberRole = (blocksOrgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (variables: { userId: string; role: OrgRole }) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return updateMemberRole(blocksOrgId, variables.userId, variables.role);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgMembers', blocksOrgId] });
      toast({ variant: 'success', title: 'Member role updated' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to update member role' });
    },
  });
};

export const useRemoveMember = (blocksOrgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (userId: string) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return removeMember(blocksOrgId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgMembers', blocksOrgId] });
      toast({ variant: 'success', title: 'Member removed from organization' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to remove member' });
    },
  });
};

export const useRevokeInvitation = (blocksOrgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (invitationId: string) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return revokeInvitation(blocksOrgId, invitationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgInvitations', blocksOrgId] });
      toast({ variant: 'success', title: 'Invitation revoked' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to revoke invitation' });
    },
  });
};

export const useResendInvitation = (blocksOrgId: string | undefined) => {
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (invitationId: string) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return resendInvitation(blocksOrgId, invitationId);
    },
    onSuccess: () => {
      toast({ variant: 'success', title: 'Invitation resent' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to resend invitation' });
    },
  });
};

export const useUpdateInvitationRoleAndResend = (blocksOrgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (variables: { invitationId: string; role: OrgRole }) => {
      if (!blocksOrgId) return Promise.reject(new Error('Organization ID is required'));
      return updateInvitationRoleAndResend(blocksOrgId, variables.invitationId, variables.role);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgInvitations', blocksOrgId] });
      toast({ variant: 'success', title: 'Invitation updated and resent' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to update and resend invitation' });
    },
  });
};
